import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { videoDownloaderApi } from '@/redux/api/tools/videoDownloaderApi'
import { Trans, useTranslation } from 'next-i18next'
import { useMount } from 'react-use'

import { VIDEO_DOWNLOADER_KEYWORD_DEFAULT } from '@/constants/tools/keyword'
import {
  testFacebookUrl,
  testTiktokUrl,
  testVkUrl,
  testYoutubeUrl,
} from '@/lib/tools/utils/test-url'
import { useSession } from 'next-auth/react'

import { TOOL } from '@/constants/tools/tool'
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from '@/constants/terms-and-policy-url'

import { ToolsLayout } from '../../common/components/ToolsLayout'
import { useToolsNotification } from '../../common/hooks/notification'

import { StepOne } from './StepOne'
import { StepTwo } from './StepTwo'
import { StepThree } from './StepThree'
import { useAmplitudeContext } from '@/components/AmplitudeContext'
import { useRouter } from 'next/router'

type VideoDownloaderProps = {
  url?: string
  /**
   * @default 'Youtube' === VIDEO_DOWNLOADER_KEYWORD_DEFAULT
   */
  keyword?: string
}

export const VideoDownloader: FC<VideoDownloaderProps> = ({
  url: urlFromProps = '',
  keyword = VIDEO_DOWNLOADER_KEYWORD_DEFAULT,
}) => {
  const { t } = useTranslation('common')
  const session = useSession()
  const { track } = useAmplitudeContext()
  const router = useRouter()

  const isLoggedIn = !!session.data?.user?.id

  const { open: openNotification, contextHolder } = useToolsNotification()

  /**
   * 지금은 어떤 오류이든 간에 같은 에러 메시지를 표시한다.
   */
  const openInvalidFormatNotification = useCallback(() => {
    openNotification(
      t('Invalid link format. Please check the URL and try again later.'),
    )
  }, [openNotification, t])

  const [url, setUrl] = useState<string>(urlFromProps)
  /**
   * URL 입력란을 한 번이라도 터치(포커스 후 블러) 했는지 여부\
   * 최초 입력 시에는 URL 포맷이 맞지 않더라도 에러로 표시하지 않기 위함임
   */
  const [isTouchedUrlInput, setIsTouchedUrlInput] = useState(false)
  /**
   * 에러 발생 이후 URL 입력을 변경하였는지 여부\
   * 에러를 리스폰스 받은 후 입력란을 변경하면 에러로 표시하지 않도록 하기 위함
   */
  const [isChangedAfterError, setIsChangedAfterError] = useState(false)

  const trimmedUrl = useMemo(() => url.trim(), [url])
  const isTiktokUrl = useMemo(() => testTiktokUrl(trimmedUrl), [trimmedUrl])
  const isYoutubeUrl = useMemo(() => testYoutubeUrl(trimmedUrl), [trimmedUrl])
  const isFacebookUrl = useMemo(() => testFacebookUrl(trimmedUrl), [trimmedUrl])
  const isVkUrl = useMemo(() => testVkUrl(trimmedUrl), [trimmedUrl])
  const isDownloadableUrl =
    isTiktokUrl || isYoutubeUrl || isFacebookUrl || isVkUrl

  const [
    requestDownload,
    {
      isLoading: isMutatingRequestDownload,
      data: resultRequestDownload,
      error: errorRequestDownload,
    },
  ] = videoDownloaderApi.useRequestDownloadMutation()

  const handleSubmit = useCallback(() => {
    if (!trimmedUrl) {
      return
    }

    if (!isDownloadableUrl) {
      return
    }

    if (isMutatingRequestDownload) {
      return
    }

    requestDownload({
      url: trimmedUrl,
    })
  }, [
    trimmedUrl,
    isDownloadableUrl,
    isMutatingRequestDownload,
    requestDownload,
  ])

  const handleDownload = useCallback(() => {
    if (isMutatingRequestDownload) {
      return
    }

    if (!resultRequestDownload) {
      return
    }

    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: 'get_converted_download',
        page_path: router.asPath,
      })
    }

    track('get_converted', {
      page_path: router.asPath,
    })

    /**
     * 로그인 한 경우 그자리에서 바로 다운로드
     */
    if (isLoggedIn) {
      const el = document.createElement('a')
      el.href = resultRequestDownload.publicUrl
      el.download = `${resultRequestDownload.videoTitle}.mp4`
      el.click()
      el.remove()
    } else {
      /**
       * 로그인하지 않은 경우 대시보드로 이동 후 다운로드
       */

      setTimeout(() => {
        /**
         * router를 사용하지 않고 location.href를 사용하는 이유
         *
         * 콜백 페이지로 이동 중 오류 발생 시 현재 페이지로 되돌아 오도록 되어 있음\
         * 이 경우 현재 컴포넌트(페이지)가 언마운트/마운트 되지 않고 프로퍼티만 변경됨\
         * 현재 기능 구현 특성 상 초기 데이터를 받아와 처리해야 하는 부분들이 있음\
         * 초기 데이터를 받아오도록 할 때 최초 한 번만 실행하도록 구현했음\
         * 그렇기 때문에 에러 후 언마운트/마운트 되지 않으면 초기 데이터 관련 동작을 하지 않음\
         * 대응 방법은 초기 데이터를 받아오는 부분을 중간에 초기 데이터가 바뀌더라도\
         * 동작하도록 구현을 변경하는 방법도 있지만\
         * 이번에는 간단하게 location.href를 사용하여 강제로 언마운트/마운트 되도록 하고\
         * 기존 구현은 그대로 유지하는 방향으로 함
         */
        window.location.href = `/dashboard?tool=${TOOL.VIDEO_DOWNLOADER}&jobId=${resultRequestDownload.jobId}`
      }, 200)
    }
  }, [
    isMutatingRequestDownload,
    resultRequestDownload,
    isLoggedIn,
    track,
    router,
  ])

  /**
   * 에러 발생(갱신) 시
   */
  useEffect(() => {
    if (!errorRequestDownload) {
      return
    }

    setIsChangedAfterError(false)
  }, [errorRequestDownload])

  /**
   * 리스폰스로 에러가 넘어온 경우 에러 토스트를 표시한다.
   */
  useEffect(() => {
    if (!errorRequestDownload) {
      return
    }

    openInvalidFormatNotification()
  }, [openInvalidFormatNotification, errorRequestDownload])

  /**
   * 입력란을 에러 상태로 표시 여부
   */
  const isShowingError = useMemo(() => {
    return (
      (!!errorRequestDownload && !isChangedAfterError) ||
      (isTouchedUrlInput && !!trimmedUrl.length && !isDownloadableUrl)
    )
  }, [
    errorRequestDownload,
    isChangedAfterError,
    isTouchedUrlInput,
    trimmedUrl,
    isDownloadableUrl,
  ])

  const errorMessage = useMemo(() => {
    if (!errorRequestDownload) {
      return ''
    }

    if (!('data' in errorRequestDownload)) {
      return ''
    }

    const data = errorRequestDownload.data as {
      error?: {
        code: string
        msg: string
      }
    }

    return data.error?.msg || ''
  }, [errorRequestDownload])

  /**
   * 에러 메시지는 우선 콘솔에만 표시한다.
   */
  useEffect(() => {
    if (!errorMessage) {
      return
    }

    console.error(errorMessage)
  }, [errorMessage])

  /**
   * URL을 쿼리로 받았으면 자동으로 다운로드 요청한다.
   */
  useMount(() => {
    if (!trimmedUrl) {
      return
    }

    /**
     * URL이 쿼리로 넘어온 상태라면 touched를 true로 바꿈
     */
    setIsTouchedUrlInput(true)

    /**
     * URL이 Youtube나 Tiktok URL이 아니라면 에러 알림을 띄우고 중단
     */
    if (!isDownloadableUrl) {
      openInvalidFormatNotification()
      return
    }

    /**
     * URL이 Youtube나 Tiktok URL이라면 자동으로 다운로드 요청
     */
    requestDownload({
      url: trimmedUrl,
    })
  })

  /**
   * 다운로드가 완료되면 프로그레스가 완료되는 애니메이션을 보여준 후 다음 단계로 넘어간다.\
   * 프로그레스가 완료되는 애니메이션의 길이는 1초이다.\
   * 길이 변경 시 여기도 반영해야 함.
   */
  const [
    isShownProgressCompletedAnimation,
    setIsShownProgressCompletedAnimation,
  ] = useState(false)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (!!resultRequestDownload) {
      timeoutId = setTimeout(() => {
        setIsShownProgressCompletedAnimation(true)
      }, 1000)
    }

    return () => clearTimeout(timeoutId)
  }, [resultRequestDownload])

  /**
   * 현재 단계
   */
  const currentStep = useMemo(() => {
    if (
      isMutatingRequestDownload ||
      // 2단계에서 3단계로 넘어갈 때는 1초의 딜레이를 준다 => 애니메이션을 끝까지 보여주기 위함
      (!!resultRequestDownload && !isShownProgressCompletedAnimation)
    ) {
      return 1
    } else if (!!resultRequestDownload) {
      return 2
    } else {
      return 0
    }
  }, [
    isMutatingRequestDownload,
    isShownProgressCompletedAnimation,
    resultRequestDownload,
  ])

  return (
    <ToolsLayout
      step={{
        currentStep,
        steps: [
          {
            title: t('Paste a URL'),
          },
          {
            title: t('Convert'),
          },
          {
            title: t('Download'),
          },
        ],
      }}
    >
      <DownloaderContainer>
        <TitleAndDescription>
          <h1>
            <span className="underline">{keyword}</span>{' '}
            <span>{t('Downloader')}</span>
          </h1>
          <div>
            <p>
              {t(
                'Download {{keyword}} videos online for free with no loss in video quality and fast speeds using the best {{keyword}} Video Downloader!',
                {
                  keyword,
                },
              )}
            </p>
          </div>
        </TitleAndDescription>

        <ContentContainer>
          {currentStep === 0 && (
            <StepOne
              url={url}
              onChangeUrl={(inputtedUrl) => {
                setUrl(inputtedUrl)
                setIsChangedAfterError(true)
              }}
              onBlurUrlInput={() => {
                setIsTouchedUrlInput(true)
              }}
              onSubmitUrl={handleSubmit}
              disabledSubmitButton={!isDownloadableUrl}
              isShowingError={isShowingError}
              keyword={keyword}
            />
          )}

          {currentStep === 1 && (
            <StepTwo isCompleted={!!resultRequestDownload} />
          )}

          {currentStep === 2 && !!resultRequestDownload && (
            <StepThree
              videoTitle={resultRequestDownload.videoTitle}
              thumbnailUrl={resultRequestDownload.thumbnailUrl}
              duration={resultRequestDownload.duration}
              onDownload={handleDownload}
            />
          )}
        </ContentContainer>

        <LicenseAndTerms>
          <Trans
            i18nKey={
              'By uploading a URL, you are agreeing to the DeepBrain AI, Inc. <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.'
            }
            components={{
              terms: <a href={TERMS_OF_SERVICE_URL} target="_blank" />,
              privacy: <a href={PRIVACY_POLICY_URL} target="_blank" />,
            }}
          />
        </LicenseAndTerms>
      </DownloaderContainer>

      {contextHolder}
    </ToolsLayout>
  )
}

const DownloaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  align-items: center;

  height: 100%;
  /* 쿠키 관련 안내가 하단에서 80px임 */
  padding: 24px 24px ${80 + 24}px;

  overflow: auto;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    padding: 10px;
    padding-top: 36px;
  }
`

const TitleAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  h1 {
    font-size: 50px;
    line-height: 58px;
    font-weight: 700;
    color: #000000;
    text-align: center;

    margin: 0;

    span.underline {
      color: #146ef5;
      letter-spacing: -0.5px;
    }
  }

  div {
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.5px;

    max-width: 600px;

    p {
      margin: 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    h1 {
      font-size: 36px;
      line-height: 50.4px;
    }
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  max-width: 900px;
  min-height: 520px;

  border: 2px solid #146ef5;
  border-radius: 20px;
  box-shadow: 0px 0px 20px 0px #146ef51a;
`

const LicenseAndTerms = styled.div`
  font-size: 13px;
  line-height: 18.2px;
  letter-spacing: -2.5%;
  text-align: center;
  color: #989898;

  a {
    color: #146ef5;
  }
`
